import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ApplicationUpdateApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import { Providers } from "../../../services/types";

interface Props {
	show: boolean;
	onHide: () => void;
	provider: Providers.GASTROSUPPLIER_MERCHANT | Providers.GASTROSUPPLIER;
}

const GastroSupplierInstallationModal = ({ show, onHide, provider }: Props) => {
	const { t } = useTranslation();
	const form = useForm<ApplicationUpdateApi>({
		criteriaMode: "all",
	});
	const {
		handleSubmit,
		setError,
		register,
		formState: { errors },
		formState,
		clearErrors,
		watch,
	} = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onSubmit = handleSubmit(async (data: ApplicationUpdateApi) => {
		if (!validateCustomErrors()) return;

		setLoading(true);
		try {
			const res = await api.organization().installApps(provider, data);
			addSuccessFlash(t("lib:common.flash.completed"));
			history.push(`/${organization.id}/apps/active/${res.id}`);
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const apiKeyValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	return (
		<Modal show={show} onHide={onHide}>
			<FormDirty
				formState={formState}
				onSubmit={(data) => {
					clearErrors();
					onSubmit(data);
				}}
			>
				<Modal.Header closeButton>
					<Modal.Title>
						<Modal.Title>
							{provider === Providers.GASTROSUPPLIER
								? t("modules.app.action.gastro_supplier_install.title")
								: t("modules.app.action.gastro_supplier_merchant_install.title")}
						</Modal.Title>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormInput
						name="settings.API_KEY"
						errors={errors}
						register={register}
						label={t("modules.app.field.api_key.title")}
						customValidationConfig={apiKeyValidationConfig}
					/>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
						{t("lib:common.action.install")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default GastroSupplierInstallationModal;
